import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { NotFoundComponent } from './modules/not-found/not-found.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    {path: '', pathMatch : 'full', redirectTo: 'home'},


    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'home'},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        
    
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'pre-orders', loadComponent: () => import('app/modules/sales-agent/sales-agent.component').then(comp => comp.SalesAgentComponent), data: { title: 'New Order - Infini-fi' }},
            {path: 'verify', loadComponent: () => import('app/modules/auth/confirmation-required/confirmation-required.component').then(comp => comp.AuthConfirmationRequiredComponent), data: { title: 'Verify Your Account - Infini-fi' }},
            {path: 'forgot-password', loadComponent: () => import('app/modules/auth/forgot-password/forgot-password.component').then(comp => comp.AuthForgotPasswordComponent), data: { title: 'Forgot Password - Infini-fi' }},
            {path: 'reset', loadComponent: () => import('app/modules/auth/reset-password/reset-password.component').then(comp => comp.AuthResetPasswordComponent), data: { title: 'Reset Password - Infini-fi' }},
            {
                path: 'sign-in', loadComponent: () => import('app/modules/auth/sign-in/sign-in.component').then(comp => comp.AuthSignInComponent),
                data: {
                    title: 'Infini-fi Login: Access Your Account',
                    description: 'Log in to your Infini-fi account to manage your internet service, view billing information, and more. Access your account here.'
                }
            },
            {path: 'sign-up', loadComponent: () => import('app/modules/auth/sign-up/sign-up.component').then(comp => comp.AuthSignUpComponent), data: {
                title: 'Create an Account - Infini-fi',
                scrollToTop: true
            }
            },

        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'sign-out', loadComponent: () => import('app/modules/auth/sign-out/sign-out.component').then(comp => comp.AuthSignOutComponent), data: { title: 'Sign Out - Infini-fi' }},
            
        ]
    },

    // Payment result page - standalone without auth guards, layout, header, or footer
    // This page is accessible to anyone for payment callbacks
    {path: 'payment-result', loadComponent: () => import('app/modules/payment-result/payment-result.component').then(comp => comp.PaymentResultComponent), data: { title: 'Payment Result - Infini-fi' }},

    {
        path: '',
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'terms-and-conditions', loadComponent: () => import('app/modules/terms-and-conditions/terms-and-conditions.component').then(comp => comp.TermsAndConditionsComponent), data: { title: 'Terms & Conditions - Infini-fi' }},
            {
                path: 'contact-us', loadComponent: () => import('app/modules/contact-us/contact-us.component').then(comp => comp.ContactUsComponent),
                data: {
                    title: 'Contact Infini-fi Support: Get in Touch for Assistance',
                    description: 'Need to contact Infini-fi support? Find our contact details here for assistance with your internet service. Reach out to our support centre for help.',
                    keywords: 'contact Infini-fi, ISP contact information, South Africa internet support, customer inquiries, Infini-fi contact',

                    ogTitle: 'Contact Infini-fi - Get in Touch with South Africa’s Leading ISP',
                    ogDescription: 'Reach out to Infini-fi for any inquiries, support requests, or information about our internet services. Our dedicated team is ready to assist you with all your connectivity needs.',
                    ogImage: 'https://my.infinifi.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://my.infinifi.co.za/contact-us',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Contact Infini-fi - Get in Touch with South Africa’s Leading ISP',
                    twDescription: 'Reach out to Infini-fi for any inquiries, support requests, or information about our internet services. Our dedicated team is ready to assist you with all your connectivity needs.',
                    twImage: 'https://my.infinifi.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://my.infinifi.co.za/contact-us'
                }
            },
            {
                path: 'support', loadComponent: () => import('app/modules/support/support.component').then(comp => comp.SupportComponent),
                data: {
                    title: 'Infini-fi Support & FAQs: Get Help with Your Internet',
                    description: 'Find answers to common questions and get support for your Infini-fiinternet. Browse our FAQs for information on installation, billing, troubleshooting, and more.',
                    keywords: 'internet support, ISP help, South Africa internet troubleshooting, Infini-fi support, customer service',

                    ogTitle: 'Infini-fi Support - Help & Resources for Your Internet Services in South Africa',
                    ogDescription: 'Get comprehensive support with Infini-fi. Access FAQs, troubleshooting tips, and contact options for assistance with your internet services. We’re here to help you with all your connectivity needs.',
                    ogImage: 'https://my.infinifi.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://my.infinifi.co.za/support',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Infini-fi Support - Help & Resources for Your Internet Services in South Africa',
                    twDescription: 'Get comprehensive support with Infini-fi. Access FAQs, troubleshooting tips, and contact options for assistance with your internet services. We’re here to help you with all your connectivity needs.',
                    twImage: 'https://my.infinifi.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://my.infinifi.co.za/support'
                }
            },
            {
                path: 'home', loadComponent: () => import('app/modules/landing/home/home.component').then(comp => comp.LandingHomeComponent),
                data: {
                    title: 'Infini-fi: Fast Internet | Free Router & Installation',
                    description: 'Upgrade to high-speed and reliable internet with Infini-fi in South Africa. Enjoy up to 10Gbps speeds, free installation, a free-to-use router, no activation fee and month-to-month flexibility.',
                    keywords: 'Infini-fi, high-speed internet South Africa, reliable ISP, affordable internet plans, home internet, business internet, internet deals, uncapped fibre, coverage check, Infini-fi login',

                    ogTitle: 'Infini-fi - Leading ISP in South Africa | High-Speed Internet Solutions',
                    ogDescription: 'Infini-fi offers high-speed internet solutions across South Africa. Discover our affordable plans and reliable connectivity options for home and business.',
                    ogImage: 'https://my.infinifi.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://my.infinifi.co.za',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Infini-fi - Leading ISP in South Africa | High-Speed Internet Solutions',
                    twDescription: 'Infini-fi offers high-speed internet solutions across South Africa. Discover our affordable plans and reliable connectivity options for home and business.',
                    twImage: 'https://my.infinifi.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://my.infinifi.co.za'
                }
            },
            {path: 'packages', loadComponent: () => import('app/modules/order-process/packages/packages.component').then(comp => comp.PackagesComponent),
                data: {
                    title: 'Infini-fi- Packages',
                }
            },
            {path: 'balances', loadComponent: () => import('app/modules/outstanding/outstanding.component').then(comp => comp.OutstandingComponent), data: { title: 'Outstanding Balances - Infini-fi' }},
            {path: 'cart', loadComponent: () => import('app/modules/order-process/cart/cart.component').then(comp => comp.CartComponent), data: { title: 'Your Cart - Infini-fi' }},
            {path: 'confirm-order', loadComponent: () => import('app/modules/order-process/confirm-order/confirm-order.component').then(comp => comp.ConfirmOrderComponent), data: { title: 'Confirm Your Order - Infini-fi' }},
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'dashboard', loadComponent: () => import('app/modules/dashboard/dashboard.component').then(comp => comp.DashboardComponent),
                data: {
                    title: 'My Dashboard - Infini-fi Fibre',
                }
            },
            {path: 'dashboard/profile', loadComponent: () => import('app/modules/dashboard/profile/profile.component').then(comp => comp.ProfileComponent),
                data: {
                    title: 'My Profile - Infini-fi Fibre',
                }
            },
            {path: 'dashboard/profile-settings', loadComponent: () => import('app/modules/dashboard/profile-settings/profile-settings.component').then(comp => comp.ProfileSettingsComponent),
                data: {
                    title: 'Profile Settings - Infini-fi Fibre',
                }
            },
            {path: 'dashboard/profile-settings/personal-info', loadComponent: () => import('app/modules/dashboard/profile-settings/personal-info/personal-info.component').then(comp => comp.PersonalInfoComponent),
                data: {
                    title: 'Personal Information - Infini-fi Fibre',
                }
            },
            {path: 'dashboard/profile-settings/manage-password', loadComponent: () => import('app/modules/dashboard/profile-settings/manage-password/manage-password.component').then(comp => comp.ManagePasswordComponent),
                data: {
                    title: 'Manage Password - Infini-fi Fibre',
                }
            },
        {path: 'dashboard/profile-settings/kyc-compliance', loadComponent: () => import('app/modules/dashboard/profile-settings/kyc-compliance/kyc-compliance.component').then(comp => comp.KycComplianceComponent),
            data: {
                title: 'KYC & Compliance - Infini-fi Fibre',
            }
        },
        {path: 'dashboard/profile-settings/delete-account', loadComponent: () => import('app/modules/dashboard/profile-settings/delete-account/delete-account.component').then(comp => comp.DeleteAccountComponent),
            data: {
                title: 'Delete Account - Infini-fi Fibre',
            }
        },
        {path: 'dashboard/faq', loadComponent: () => import('app/modules/dashboard/faq/faq.component').then(comp => comp.FaqComponent),
            data: {
                title: 'FAQs - Infini-fi Fibre',
            }
        },
        {path: 'dashboard/faq/:id', loadComponent: () => import('app/modules/dashboard/faq/faq-questions/faq-questions.component').then(comp => comp.FaqQuestionsComponent),
            data: {
                title: 'FAQ Questions - Infini-fi Fibre',
            }
        },
        {path: 'dashboard/help-centre', loadComponent: () => import('app/modules/dashboard/help-centre/help-centre.component').then(comp => comp.HelpCentreComponent),
            data: {
                title: 'Help Centre - Infini-fi Fibre',
            }
        },
        {path: 'dashboard/help-centre/:id', loadComponent: () => import('app/modules/dashboard/help-centre/help-centre-category/help-centre-category.component').then(comp => comp.HelpCentreCategoryComponent),
            data: {
                title: 'Help Centre Category - Infini-fi Fibre',
            }
        },
        {path: 'dashboard/help-centre/:id/:slug', loadComponent: () => import('app/modules/dashboard/help-centre/help-centre-article/help-centre-article.component').then(comp => comp.HelpCentreArticleComponent),
            data: {
                title: 'Help Centre Article - Infini-fi Fibre',
            }
        },
            {path: 'dashboard/requests', loadComponent: () => import('app/modules/dashboard/requests/requests.component').then(comp => comp.RequestsComponent),
                data: {
                    title: 'My Tickets - Infini-fi Fibre',
                }
            },
            {path: 'dashboard/ticket-comments/:id', loadComponent: () => import('app/modules/dashboard/ticket-comments/ticket-comments.component').then(comp => comp.TicketCommentsComponent),
                data: {
                    title: 'Support Ticket Comments - Infini-fi Fibre',
                }
            },
            {path: 'dashboard/invoices', loadComponent: () => import('app/modules/dashboard/invoices/invoices.component').then(comp => comp.InvoicesComponent),
                data: {
                    title: 'Billing - Infini-fi Fibre',
                }
            },
            {path: 'dashboard/orders', loadComponent: () => import('app/modules/dashboard/orders-page/orders-page.component').then(comp => comp.OrdersPageComponent),
                data: {
                    title: 'My Orders - Infini-fi Fibre',
                }
            },
            {path: 'dashboard/services', loadComponent: () => import('app/modules/dashboard/services-page/services-page.component').then(comp => comp.ServicesPageComponent),
                data: {
                    title: 'My Services - Infini-fi Fibre',
                }
            },
            {path: 'dashboard/settings', loadComponent: () => import('app/modules/dashboard/settings/settings.component').then(comp => comp.SettingsComponent),
                data: {
                    title: 'Settings - Infini-fi Fibre',
                }
            },
            {path: 'dashboard/faq', loadComponent: () => import('app/modules/dashboard/faq/faq.component').then(comp => comp.FaqComponent),
                data: {
                    title: 'FAQs - Infini-fi Fibre',
                }
            },
        ]
    },

   

    // 404
    {path: '**', pathMatch: 'full', component: NotFoundComponent},
]
